<script setup lang="ts">
  // Extracted verbatim from PosV2Cart.vue's footer so the totals block can
  // render inline (tier 0) or inside the Detalle chip popover (tier >= 1).
  import type { IPosOrderTotals } from '@/domain/pos/pos-types';
  import { usePosMoney } from '@/composition/use-pos-money';

  withDefaults(
    defineProps<{
      // Optional so Vue 2 skips runtime type-checks when null is passed.
      totals?: IPosOrderTotals | null;
    }>(),
    {
      totals: null,
    }
  );

  const money = usePosMoney();
</script>

<template>
  <div class="cart-totals">
    <div class="total-row">
      <span class="total-label">{{ $t('posV2.products') }}</span>
      <span class="total-value">
        {{ money.formatComputedMoney(totals?.products || 0) }}
      </span>
    </div>
    <div v-if="(totals?.delivery || 0) > 0" class="total-row">
      <span class="total-label">{{ $t('posV2.deliveryFee') }}</span>
      <span class="total-value">
        {{ money.formatComputedMoney(totals?.delivery || 0) }}
      </span>
    </div>
    <div v-if="(totals?.productsTax || 0) > 0" class="total-row">
      <span class="total-label">{{ $t('posV2.taxes') }}</span>
      <span class="total-value">
        {{
          money.formatComputedMoney(
            (totals?.productsTax || 0) + (totals?.deliveryTax || 0)
          )
        }}
      </span>
    </div>
    <div v-if="(totals?.discountedAmount || 0) > 0" class="total-row">
      <span class="total-label">{{ $t('posV2.discount') }}</span>
      <span class="total-value discount">
        -{{ money.formatComputedMoney(totals?.discountedAmount || 0) }}
      </span>
    </div>
    <div v-if="(totals?.roundDifference || 0) !== 0" class="total-row">
      <span class="total-label">{{ $t('posV2.rounding') }}</span>
      <span class="total-value">
        {{ money.formatComputedMoney(totals?.roundDifference || 0) }}
      </span>
    </div>
    <div class="total-row grand">
      <span class="total-label">{{ $t('posV2.total') }}</span>
      <span class="total-value">
        {{ money.formatComputedMoney(totals?.total || 0) }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .cart-totals {
    @apply tw-flex tw-flex-col tw-gap-1 tw-border-t tw-border-neutral-200 tw-pt-2;

    .total-row {
      @apply tw-flex tw-items-center tw-justify-between tw-text-xs;

      &.grand {
        @apply tw-mt-1.5 tw-border-t tw-border-neutral-200 tw-pt-1.5 tw-text-base tw-font-bold;
      }

      .total-label {
        @apply tw-text-neutral-700;
      }

      .total-value {
        @apply tw-font-semibold tw-text-neutral-900;

        &.discount {
          @apply tw-text-emerald-600;
        }
      }
    }
  }
</style>
